import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { URL_SORT_DIRECTION, URL_SORT_KEY } from "constants/url_query_keys";
import { SearchQueryOrderDirection } from "types/generated";

/**
 * Hook to initialize URL sort parameters if they don't exist
 *
 * This hook ensures that default sort parameters are set in the URL when a component loads,
 * which is useful for client-side table sorting.
 *
 * @param initialSortOption - The default column to sort by
 * @param initialSortDirection - The default sort direction (asc or desc)
 */
export const useInitialSort = (
  initialSortOption: string,
  initialSortDirection: SearchQueryOrderDirection
): void => {
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const sortKey = searchParams.get(URL_SORT_KEY);
    const sortDirection = searchParams.get(URL_SORT_DIRECTION);

    if (!sortKey || !sortDirection) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set(URL_SORT_KEY, initialSortOption);
      newParams.set(URL_SORT_DIRECTION, initialSortDirection);
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams, initialSortOption, initialSortDirection]);
};
